import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { HttpClient } from '@angular/common/http';
import { Subject, of } from 'rxjs';
import {
  takeUntil,
  catchError,
  take,
  filter,
  timeout,
  finalize,
} from 'rxjs/operators';
import { environment } from '../../../environment/environment';
import { UserService } from '../../../services/user/user.service';
import { AuthService } from '../../../services/auth/auth.service';
import { ActivatedRoute, Router } from '@angular/router';
import {
  DUNA_CATERING_PREFERENCE_OPTIONS,
  normalizeCateringPreferenceSelection,
} from '../../../constants/duna-catering-preference-options';

interface CateringPreferencesResponse {
  catering_preferences?: string[];
  catering_preferences_form?: string[];
}

@Component({
  selector: 'app-duna-user-catering-preferences',
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './duna-user-catering-preferences.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./duna-user-catering-preferences.component.scss'],
})
export class DunaUserCateringPreferencesComponent implements OnInit, OnDestroy {
  readonly preferenceOptions = DUNA_CATERING_PREFERENCE_OPTIONS;

  targetUuid = '';
  displayName = '';
  selectedPreferences: string[] = [];
  loading = true;
  saving = false;
  errorMessage: string | null = null;
  accessDenied = false;
  isEditingOtherUser = false;
  isOnProfilePage = false;

  private destroy$ = new Subject<void>();

  constructor(
    private http: HttpClient,
    private userService: UserService,
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {}

  private detectProfilePage(): boolean {
    return /\/profile(\/|$)/i.test((this.router.url || '').split('?')[0]);
  }

  private getApiBaseUrl(): string {
    let base = (environment.apiUrl || '').replace(/\/$/, '');
    if (base && !/^https?:\/\//i.test(base)) {
      base = 'https://' + base;
    }
    return base;
  }

  ngOnInit(): void {
    this.isOnProfilePage = this.detectProfilePage();

    this.authService.authStateReady$
      .pipe(
        filter((ready) => ready),
        take(1),
        timeout(15000),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => this.resolveTargetAndLoad(),
        error: () => this.resolveTargetAndLoad(),
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private resolveTargetUuid(): string | null {
    const fromUrl = this.userService.getCurrentUserUuid();
    if (fromUrl) {
      return fromUrl;
    }
    const queryUuid = this.route.snapshot.queryParamMap.get('user');
    if (queryUuid) {
      return queryUuid;
    }
    return this.authService.getCurrentUser()?.uuid ?? null;
  }

  private resolveTargetAndLoad(): void {
    const uuid = this.resolveTargetUuid();
    if (!uuid) {
      this.loading = false;
      this.errorMessage = 'Kunne ikke finde bruger.';
      this.cdr.markForCheck();
      return;
    }

    this.targetUuid = uuid;
    const selfUuid = this.authService.getCurrentUser()?.uuid;
    this.isEditingOtherUser = !!(selfUuid && selfUuid !== uuid);

    const base = this.getApiBaseUrl();
    if (!base) {
      this.loading = false;
      this.errorMessage = 'API-adresse mangler.';
      this.cdr.markForCheck();
      return;
    }

    this.http
      .get<CateringPreferencesResponse>(
        `${base}/users/${uuid}/catering-preferences`
      )
      .pipe(
        timeout(20000),
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (prefs) => {
          this.applyPreferencesResponse(prefs);
          if (this.isEditingOtherUser) {
            this.userService
              .getUserByUuid(uuid)
              .pipe(
                catchError(() => of(null)),
                takeUntil(this.destroy$)
              )
              .subscribe((user) => {
                if (user) {
                  this.displayName = [user.first_name, user.last_name]
                    .filter(Boolean)
                    .join(' ')
                    .trim();
                  this.cdr.markForCheck();
                }
              });
          }
        },
        error: (err) => {
          this.setErrorFromResponse(err, 'Kunne ikke hente kostpræferencer.');
        },
      });
  }

  private applyPreferencesResponse(
    prefs: CateringPreferencesResponse | null
  ): void {
    const formPrefs = prefs?.catering_preferences_form;
    const normalized = prefs?.catering_preferences;
    if (Array.isArray(formPrefs) && formPrefs.length > 0) {
      this.selectedPreferences =
        normalizeCateringPreferenceSelection(formPrefs);
    } else if (Array.isArray(normalized)) {
      this.selectedPreferences = normalizeCateringPreferenceSelection(
        normalized.map((p) =>
          p === 'ingen_praeferencer' ? 'nothing' : p === 'halal' ? 'hallal' : p
        )
      );
    } else {
      this.selectedPreferences = [];
    }
  }

  isSelected(value: string): boolean {
    return this.selectedPreferences.includes(value);
  }

  togglePreference(value: string, checked: boolean): void {
    let next = [...this.selectedPreferences];
    if (value === 'nothing') {
      next = checked ? ['nothing'] : [];
    } else if (checked) {
      next = next.filter((v) => v !== 'nothing');
      if (!next.includes(value)) {
        next.push(value);
      }
    } else {
      next = next.filter((v) => v !== value);
    }
    this.selectedPreferences = normalizeCateringPreferenceSelection(next);
  }

  save(): void {
    if (!this.targetUuid || this.saving) {
      return;
    }
    const base = this.getApiBaseUrl();
    if (!base) {
      return;
    }

    this.saving = true;
    this.errorMessage = null;
    this.http
      .put<CateringPreferencesResponse>(
        `${base}/users/${this.targetUuid}/catering-preferences`,
        {
          catering_preferences: this.selectedPreferences,
        }
      )
      .pipe(
        timeout(20000),
        takeUntil(this.destroy$),
        finalize(() => {
          this.saving = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (res) => {
          this.applyPreferencesResponse(res);
          this.snackBar.open('Kostpræferencer gemt', undefined, {
            duration: 2500,
          });
        },
        error: (err) => {
          this.setErrorFromResponse(err, 'Kunne ikke gemme kostpræferencer.');
          this.snackBar.open(this.errorMessage ?? 'Fejl', undefined, {
            duration: 4000,
          });
        },
      });
  }

  private setErrorFromResponse(
    err: { status?: number },
    fallback: string
  ): void {
    if (err?.status === 403) {
      this.accessDenied = true;
      this.errorMessage = 'Du har ikke adgang.';
      return;
    }
    this.errorMessage = fallback;
  }
}
